import React from 'react';
import { Link } from 'react-router-dom';
import {
  Lightbulb,
  PenTool,
  Shield,
  Rocket,
  CheckCircle,
  Zap,
  BarChart3,
  Users,
  ArrowRight,
} from 'lucide-react';
import SEO from '../components/SEO';

const phases = [
  {
    number: '01',
    icon: Lightbulb,
    title: 'Ideation & Validation',
    description: 'Comprehensive market research, user needs analysis, and feasibility studies to validate your concept.',
    features: [
      'Market opportunity assessment',
      'User needs identification & analysis',
      'Concept development & feasibility',
      'Stakeholder alignment & IP strategy',
    ],
  },
  {
    number: '02',
    icon: PenTool,
    title: 'Design & Development',
    description: 'Rigorous engineering design with iterative prototyping and verification & validation testing.',
    features: [
      'Rapid prototyping & iteration',
      'Design controls & documentation',
      'Verification & validation testing',
      'Human factors engineering',
    ],
  },
  {
    number: '03',
    icon: Shield,
    title: 'Regulatory & Quality',
    description: 'Expert regulatory strategy and quality management to ensure compliance across global markets.',
    features: [
      'FDA, TGA & CE Mark submissions',
      'Quality management systems (ISO 13485)',
      'Clinical evaluation & evidence',
      'Risk management (ISO 14971)',
    ],
  },
  {
    number: '04',
    icon: Rocket,
    title: 'Scale & Commercialization',
    description: 'From manufacturing setup to market launch and ongoing post-market surveillance.',
    features: [
      'Manufacturing transfer & scale-up',
      'Market launch strategy',
      'Post-market surveillance',
      'Continuous improvement & lifecycle',
    ],
  },
];

const benefits = [
  { icon: Zap, title: 'Accelerated Time-to-Market', description: 'Streamlined processes that reduce development cycles without compromising quality.' },
  { icon: Shield, title: 'Risk Mitigation', description: 'Proactive risk management integrated at every stage of development.' },
  { icon: BarChart3, title: 'Optimized Resources', description: 'Efficient allocation of resources to maximize ROI across the development lifecycle.' },
  { icon: Users, title: 'Expert Support', description: 'Access to a multidisciplinary team with deep domain expertise.' },
];

const Sketch2Scale = () => {
  return (
    <>
      <SEO
        title="Sketch2Scale"
        description="End-to-end product development from ideation and validation through design, regulatory approval, and market launch."
        path="/sketch2scale"
      />
      <div>
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-[120px] -top-40 -right-40" />
        <div className="absolute w-[400px] h-[400px] bg-indigo-500/10 rounded-full blur-[120px] bottom-0 -left-40" />
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-cyan-400">Our Methodology</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight mt-4">
            From <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-indigo-400">Sketch to Scale</span>
          </h1>
          <p className="mt-6 text-lg text-slate-400 max-w-2xl mx-auto">
            Our proven four-phase framework takes medical device concepts from initial ideation through to global commercialization, with expert guidance at every step.
          </p>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 max-w-7xl mx-auto px-6">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-slate-300 text-lg leading-relaxed">
            The Sketch2Scale framework is built on decades of experience bringing medical devices to market across three continents.
            Each phase is designed to reduce risk, accelerate timelines, and ensure regulatory compliance from day one.
          </p>
        </div>
      </section>

      {/* Four Phases */}
      <section className="py-24 bg-[#111827]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-cyan-400">The Journey</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mt-3">
              Four Phases to Market
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {phases.map((phase) => {
              const Icon = phase.icon;
              return (
                <div
                  key={phase.number}
                  className="bg-white/[0.04] backdrop-blur-xl border border-white/[0.06] rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1 hover:bg-white/[0.07] hover:shadow-[0_20px_60px_rgba(14,165,233,0.12)]"
                >
                  <div className="flex items-center gap-4 mb-6">
                    <span className="text-4xl font-display font-bold bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-indigo-400">
                      {phase.number}
                    </span>
                    <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center">
                      <Icon className="w-6 h-6 text-cyan-400" />
                    </div>
                  </div>
                  <h3 className="font-display text-xl font-bold text-white mb-3">{phase.title}</h3>
                  <p className="text-slate-400 mb-6">{phase.description}</p>
                  <ul className="space-y-3">
                    {phase.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-300 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-cyan-400">Why Sketch2Scale</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mt-3">
            Built for Success
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {benefits.map((benefit) => {
            const Icon = benefit.icon;
            return (
              <div
                key={benefit.title}
                className="bg-white/[0.04] backdrop-blur-xl border border-white/[0.06] rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1 hover:bg-white/[0.07] hover:shadow-[0_20px_60px_rgba(14,165,233,0.12)]"
              >
                <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center mb-6">
                  <Icon className="w-6 h-6 text-cyan-400" />
                </div>
                <h3 className="font-display text-lg font-bold text-white mb-2">{benefit.title}</h3>
                <p className="text-slate-400 text-sm">{benefit.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 max-w-7xl mx-auto px-6">
        <div className="relative rounded-3xl overflow-hidden p-16 text-center bg-gradient-to-r from-cyan-600/20 via-indigo-600/20 to-cyan-600/20">
          <div className="absolute w-[300px] h-[300px] bg-cyan-500/10 rounded-full blur-[100px] -top-20 -right-20" />
          <div className="absolute w-[300px] h-[300px] bg-indigo-500/10 rounded-full blur-[100px] -bottom-20 -left-20" />
          <div className="relative">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white tracking-tight">
              Ready to Bring Your Device to Market?
            </h2>
            <p className="mt-4 text-slate-300 text-lg max-w-xl mx-auto">
              Let's discuss how our Sketch2Scale framework can accelerate your medical device development journey.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 mt-8 px-8 py-4 rounded-xl font-display font-semibold text-white text-sm transition-all duration-300 hover:-translate-y-0.5"
              style={{ background: 'linear-gradient(135deg, #0ea5e9, #06b6d4)', boxShadow: '0 0 30px rgba(14, 165, 233, 0.25)' }}
            >
              Get Started <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
      </div>
    </>
  );
};

export default Sketch2Scale;
